import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimationBox from '../../components/common/AnimationBox';
import Header from '../../components/layout/Header';
import { ReCaptcha } from '../../components/ui';
import { initializeServices } from '../../services';
import { useAuthStore } from '../../stores/authStore';
import { auth, googleProvider, microsoftProvider, facebookProvider } from '../../config/firebase';
import { signInWithPopup } from 'firebase/auth';
import '../../styles/LoginPage.css';
import '../../styles/App.css';

const SignupPage = ({ mode = 'signup' }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authService, setAuthService] = useState(null);

  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  // Configuration based on mode
  const config = {
    signup: {
      title: 'Sign Up',
      purpose: 'sign_up',
      nextRoute: '/otp-verification',
      bottomText: 'Already have an account?',
      bottomLinkText: 'Sign in',
      bottomLinkRoute: '/login',
      showSocialLogin: true,
      submitText: 'Continue',
      loadingText: 'Sending...'
    },
    'forgot-password': {
      title: 'Forget Password',
      purpose: 'forgot_password',
      nextRoute: '/otp-verification',
      bottomText: 'Remember your password?',
      bottomLinkText: 'Sign In',
      bottomLinkRoute: '/login',
      showSocialLogin: false,
      loadingText: 'Sending Reset Code...',
      submitText:'Send Code to reset'
    }
  };

  const currentConfig = config[mode] || config.signup;

  // Initialize services
  useEffect(() => {
    const initServices = async () => {
      try {
        const services = await initializeServices();
        setAuthService(services.authService);
      } catch (error) {
        console.error('Failed to initialize services:', error);
        setError('Failed to initialize application. Please refresh the page.');
      }
    };

    initServices();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setCaptchaError('');

    let valid = true;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      valid = false;
    }

    if (!captchaToken) {
      setCaptchaError('Please complete the CAPTCHA.');
      valid = false;
    }

    if (!authService) {
      setError('Service not available. Please refresh the page.');
      valid = false;
    }

    if (!valid) return;

    setIsLoading(true);

    try {
      // Call the email verification API with the appropriate purpose
      const result = await authService.sendEmailVerification(email, captchaToken, currentConfig.purpose);

      if (result.success) {
        // Enhanced debug logging to see exact response structure
        console.log(`📧 ${currentConfig.title} - Email Verification Success - Full Response:`, result);
        console.log(`📧 ${currentConfig.title} - Data Object:`, result.data);
        console.log(`📧 ${currentConfig.title} - Nested Data:`, result.data?.data);
        console.log(`📧 ${currentConfig.title} - Token (data.token):`, result.data?.token);
        console.log(`📧 ${currentConfig.title} - Token (data.data.token):`, result.data?.data?.token);

        // Try both possible token locations
        const extractedToken = result.data?.data?.token || result.data?.token || '';

        console.log(`📧 ${currentConfig.title} - Extracted Token:`, {
          email,
          token: extractedToken,
          tokenLength: extractedToken.length,
          hasToken: !!extractedToken,
          tokenSource: result.data?.data?.token ? 'data.data.token' : (result.data?.token ? 'data.token' : 'not found'),
          purpose: currentConfig.purpose
        });

        // Navigate to OTP verification screen on success
        // Pass email, verification token, and purpose
        navigate(currentConfig.nextRoute, {
          state: {
            email,
            verificationToken: extractedToken,
            purpose: currentConfig.purpose
          }
        });
      } else {
        // Show only the backend error message
        if (result.error) {
          setError(result.error);
        }

        // Reset reCAPTCHA on error
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setCaptchaToken(null);
        }
      }
    } catch (error) {
      console.error('Email verification error:', error);
      // Show only the backend error message
      setError(error.response?.data?.message || error.response?.data?.error || error.message);

      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaToken(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Header />
      <AnimationBox className="login-box">
        <h4 className='h4 '>{currentConfig.title}</h4>

        {currentConfig.showSocialLogin && (
          <>
            <div className='social-login'>
              <button className='social-btn google body2'>Continue with Google<img src='/icons/google.svg' alt='google' /></button>
              <button className='social-btn apple body2'>Continue with Apple<img src='/icons/apple.svg' alt='apple' /></button>
              <button className='social-btn microsoft body2'>Continue with Microsoft<img src='/icons/microsoft.svg' alt='microsoft' /></button>
              <button className='social-btn facebook body2'>Continue with Facebook<img src='/icons/facebook.svg' alt='facebook' /></button>
            </div>

            <div className="divider"><span>OR</span></div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="text"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(''); // Clear error when user starts typing
              }}
              placeholder="Enter your email address..."
            />
            {error && <div className="error-message">{error}</div>}
          </div>

          <ReCaptcha
            ref={recaptchaRef}
            onChange={(token) => setCaptchaToken(token)}
            showError={false}
          />
          {captchaError && <div className="error-message">{captchaError}</div>}

          <button
            type="submit"
            className="body3-bold login-button"
            disabled={isLoading || !authService}
          >
            {isLoading ? currentConfig.loadingText : currentConfig.submitText}
          </button>
        </form>

        <hr className="hr" />
        <div className="signup">
          <span className='body2'>{currentConfig.bottomText}</span>
          <button
            className='body3-bold'
            onClick={() => navigate(currentConfig.bottomLinkRoute)}
          >
            {currentConfig.bottomLinkText}
          </button>
        </div>
      </AnimationBox>
    </div>
  );
};

export default SignupPage;
